import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ChevronLeft, Check, Calendar as CalIcon, Clock, MapPin, Sparkles } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS = (() => {
  const out = [];
  for (let h = 6; h < 22; h++) {
    out.push(`${String(h).padStart(2, '0')}:00`);
    out.push(`${String(h).padStart(2, '0')}:30`);
  }
  out.push('22:00');
  return out;
})();

function nextDays(n) {
  const out = [];
  const today = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    out.push(d);
  }
  return out;
}

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function QuickBookPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();

  const [step, setStep] = useState(1); // 1=space, 2=date, 3=time, 4=confirm
  const [spaces, setSpaces] = useState([]);
  const [loadingSpaces, setLoadingSpaces] = useState(true);
  const [spaceId, setSpaceId] = useState('');
  const [date, setDate] = useState(fmtDate(new Date()));
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('11:00');
  const [submitting, setSubmitting] = useState(false);
  const [busySlots, setBusySlots] = useState([]);

  const days = useMemo(() => nextDays(14), []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/auth?redirect=/quick-book');
    }
  }, [authLoading, user, navigate]);

  // Load spaces
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/spaces`);
        if (!cancelled) setSpaces(res.data || []);
      } catch {
        if (!cancelled) toast.error(t('quickBook.loadSpacesError', 'Impossible de charger les espaces.'));
      } finally {
        if (!cancelled) setLoadingSpaces(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  // Load availability whenever space+date change (step >= 3)
  useEffect(() => {
    if (!spaceId || !date || step < 3) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/spaces/${spaceId}/availability?date=${date}`
        );
        if (!cancelled) {
          // Backend returns { booked_slots: [{start_time, end_time}] }
          const items = res.data?.booked_slots || [];
          const slots = new Set();
          items.forEach((b) => {
            // Mark every 30-min slot inside [start, end[ as busy
            const s = SLOTS.indexOf(b.start_time);
            const e = SLOTS.indexOf(b.end_time);
            if (s >= 0 && e >= 0) for (let i = s; i < e; i++) slots.add(SLOTS[i]);
          });
          setBusySlots(Array.from(slots));
        }
      } catch {
        if (!cancelled) setBusySlots([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [spaceId, date, step]);

  const selectedSpace = useMemo(() => spaces.find((s) => s.space_id === spaceId), [spaces, spaceId]);

  const startIdx = SLOTS.indexOf(startTime);
  const endIdx = SLOTS.indexOf(endTime);
  const validRange = startIdx >= 0 && endIdx > startIdx;
  const halfHours = validRange ? endIdx - startIdx : 0;
  const hours = halfHours / 2;
  const VAT_MULT = 1.21;
  const price = useMemo(() => {
    if (!selectedSpace || !validRange) return 0;
    return Math.round(Number(selectedSpace.price_hourly || 0) * hours * VAT_MULT * 100) / 100;
  }, [selectedSpace, hours, validRange]);

  // Disable end-times that are <= startTime, and grey-out busy slots
  const startSlots = SLOTS.slice(0, -1); // last slot can't be a start
  const endSlots = SLOTS.slice(1); // first slot can't be an end

  function isBusyForStart(slot) {
    return busySlots.includes(slot);
  }

  async function submit() {
    if (!selectedSpace || !validRange) return;
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const bookRes = await axios.post(
        `${API}/bookings`,
        {
          space_id: spaceId,
          date,
          start_time: startTime,
          end_time: endTime,
          booking_type: 'hourly',
          use_free_hours: 0,
          voucher_code: null,
        },
        { headers, withCredentials: true }
      );
      const booking = bookRes.data;
      const payRes = await axios.post(
        `${API}/payments/checkout`,
        { booking_id: booking.booking_id, origin_url: window.location.origin },
        { headers, withCredentials: true }
      );
      if (payRes.data?.free) {
        toast.success(t('quickBook.confirmedFree', 'Réservation confirmée !'));
        navigate('/mes-acces');
        return;
      }
      window.location.href = payRes.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t('quickBook.bookingError', 'Erreur lors de la réservation.'));
    } finally {
      setSubmitting(false);
    }
  }

  if (authLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A3C34]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-32" data-testid="quick-book-page">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 bg-[#1A3C34] text-white px-4 py-3 shadow-md">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => (step > 1 ? setStep(step - 1) : navigate('/m'))}
            className="text-white/70 hover:text-white p-1.5"
            data-testid="quick-back-btn"
            aria-label="Back"
          >
            <ChevronLeft size={22} />
          </button>
          <div className="flex-1">
            <h1 className="font-heading text-base sm:text-lg font-bold">
              {t('quickBook.title', 'Réservation rapide')}
            </h1>
            <p className="text-[11px] text-white/60">
              {t('quickBook.step', 'Étape')} {step}/4
            </p>
          </div>
          <Sparkles size={18} className="text-[#C77D63]" />
        </div>

        {/* Progress bar */}
        <div className="max-w-xl mx-auto mt-2 flex gap-1">
          {[1, 2, 3, 4].map((n) => (
            <div
              key={n}
              className={`flex-1 h-1 rounded-full ${n <= step ? 'bg-[#C77D63]' : 'bg-white/10'}`}
            />
          ))}
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 pt-5 space-y-4">
        {/* Step 1 — Space */}
        {step === 1 && (
          <section data-testid="quick-step-space">
            <h2 className="font-heading text-lg font-bold text-[#1A3C34] mb-1">
              {t('quickBook.chooseSpace', 'Choisissez un espace')}
            </h2>
            <p className="text-xs text-[#1A3C34]/60 mb-4">
              {t('quickBook.chooseSpaceSub', 'Sélectionnez l\'espace adapté à votre activité.')}
            </p>
            {loadingSpaces ? (
              <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#1A3C34]" /></div>
            ) : (
              <div className="space-y-2">
                {spaces.map((s) => (
                  <button
                    key={s.space_id}
                    type="button"
                    onClick={() => { setSpaceId(s.space_id); setStep(2); }}
                    data-testid={`quick-space-${s.space_id}`}
                    className={`w-full text-left bg-white rounded-lg p-4 border-2 transition-all ${
                      spaceId === s.space_id ? 'border-[#C77D63]' : 'border-[#1A3C34]/10 hover:border-[#1A3C34]/30'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-base font-bold text-[#1A3C34] truncate">
                          {s.name}
                        </h3>
                        <p className="text-xs text-[#1A3C34]/60 mt-0.5 line-clamp-2">{s.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-[11px] text-[#1A3C34]/60">
                          {s.capacity && (<span>👥 {s.capacity}</span>)}
                          <span className="font-semibold text-[#C77D63]">
                            {Number(s.price_hourly || 0).toFixed(0)} €/h HT
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Step 2 — Date */}
        {step === 2 && (
          <section data-testid="quick-step-date">
            <h2 className="font-heading text-lg font-bold text-[#1A3C34] mb-1">
              {t('quickBook.chooseDate', 'Choisissez une date')}
            </h2>
            <p className="text-xs text-[#1A3C34]/60 mb-4">
              {selectedSpace?.name}
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {days.map((d) => {
                const ds = fmtDate(d);
                const isSel = ds === date;
                return (
                  <button
                    key={ds}
                    type="button"
                    onClick={() => { setDate(ds); setStep(3); }}
                    data-testid={`quick-date-${ds}`}
                    className={`p-3 rounded-lg text-center transition-all border-2 ${
                      isSel ? 'bg-[#1A3C34] text-white border-[#1A3C34]' : 'bg-white text-[#1A3C34] border-[#1A3C34]/10 hover:border-[#1A3C34]/30'
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-wide opacity-60">
                      {d.toLocaleDateString(i18n.language, { weekday: 'short' })}
                    </div>
                    <div className="font-heading text-2xl font-bold leading-tight">
                      {d.getDate()}
                    </div>
                    <div className="text-[10px] opacity-60">
                      {d.toLocaleDateString(i18n.language, { month: 'short' })}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Step 3 — Time */}
        {step === 3 && (
          <section data-testid="quick-step-time">
            <h2 className="font-heading text-lg font-bold text-[#1A3C34] mb-1">
              {t('quickBook.chooseTime', 'Choisissez l\'horaire')}
            </h2>
            <p className="text-xs text-[#1A3C34]/60 mb-4">
              {selectedSpace?.name} — {new Date(date).toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>

            {/* Start time */}
            <h3 className="text-xs font-semibold text-[#1A3C34]/70 uppercase tracking-wide mb-2">
              {t('quickBook.start', 'Début')}
            </h3>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 mb-5">
              {startSlots.map((slot) => {
                const busy = isBusyForStart(slot);
                const sel = startTime === slot;
                return (
                  <button
                    key={slot}
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setStartTime(slot);
                      // auto-bump end if needed
                      const si = SLOTS.indexOf(slot);
                      if (SLOTS.indexOf(endTime) <= si) {
                        setEndTime(SLOTS[Math.min(si + 2, SLOTS.length - 1)]);
                      }
                    }}
                    data-testid={`quick-start-${slot}`}
                    className={`py-2 px-1 text-xs rounded font-mono transition-colors ${
                      busy ? 'bg-gray-100 text-gray-300 line-through cursor-not-allowed' :
                      sel ? 'bg-[#C77D63] text-white' : 'bg-white text-[#1A3C34] hover:bg-[#1A3C34]/5 border border-[#1A3C34]/10'
                    }`}
                  >
                    {slot}
                  </button>
                );
              })}
            </div>

            {/* End time */}
            <h3 className="text-xs font-semibold text-[#1A3C34]/70 uppercase tracking-wide mb-2">
              {t('quickBook.end', 'Fin')}
            </h3>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 mb-5">
              {endSlots.map((slot) => {
                const idx = SLOTS.indexOf(slot);
                const tooEarly = idx <= startIdx;
                // also disable if any slot between start and end is busy
                let conflicts = false;
                for (let i = startIdx; i < idx; i++) {
                  if (busySlots.includes(SLOTS[i])) { conflicts = true; break; }
                }
                const disabled = tooEarly || conflicts;
                const sel = endTime === slot;
                return (
                  <button
                    key={slot}
                    type="button"
                    disabled={disabled}
                    onClick={() => setEndTime(slot)}
                    data-testid={`quick-end-${slot}`}
                    className={`py-2 px-1 text-xs rounded font-mono transition-colors ${
                      disabled ? 'bg-gray-100 text-gray-300 cursor-not-allowed' :
                      sel ? 'bg-[#C77D63] text-white' : 'bg-white text-[#1A3C34] hover:bg-[#1A3C34]/5 border border-[#1A3C34]/10'
                    }`}
                  >
                    {slot}
                  </button>
                );
              })}
            </div>

            <Button
              onClick={() => setStep(4)}
              disabled={!validRange}
              data-testid="quick-next-btn"
              className="w-full h-12 bg-[#C77D63] hover:bg-[#B86F58] text-white"
            >
              {t('quickBook.continue', 'Continuer')} — {price.toFixed(2)} €
            </Button>
          </section>
        )}

        {/* Step 4 — Confirm */}
        {step === 4 && (
          <section data-testid="quick-step-confirm">
            <h2 className="font-heading text-lg font-bold text-[#1A3C34] mb-4">
              {t('quickBook.recap', 'Récapitulatif')}
            </h2>
            <div className="bg-white rounded-xl border border-[#1A3C34]/10 p-5 space-y-3 mb-5">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#C77D63] mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wide text-[#1A3C34]/40">{t('quickBook.space', 'Espace')}</p>
                  <p className="text-sm font-semibold text-[#1A3C34]">{selectedSpace?.name}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CalIcon className="w-4 h-4 text-[#C77D63] mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wide text-[#1A3C34]/40">{t('quickBook.date', 'Date')}</p>
                  <p className="text-sm font-semibold text-[#1A3C34]">
                    {new Date(date).toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long' })}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-[#C77D63] mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wide text-[#1A3C34]/40">{t('quickBook.duration', 'Durée')}</p>
                  <p className="text-sm font-semibold text-[#1A3C34]">{startTime} – {endTime} ({hours} h)</p>
                </div>
              </div>
              <div className="border-t border-[#1A3C34]/10 pt-3">
                {(() => {
                  const ht = Math.round(price / 1.21 * 100) / 100;
                  const tva = Math.round((price - ht) * 100) / 100;
                  return (
                    <>
                      <div className="flex justify-between items-baseline text-xs text-[#1A3C34]/70 mb-0.5">
                        <span>{t('quickBook.subtotalHT', 'Sous-total HT')}</span>
                        <span className="font-mono">{ht.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between items-baseline text-xs text-[#1A3C34]/70 mb-1.5">
                        <span>{t('quickBook.vat', 'TVA 21%')}</span>
                        <span className="font-mono">+{tva.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between items-center pt-1.5 border-t border-[#1A3C34]/10">
                        <span className="text-sm text-[#1A3C34]/70">{t('quickBook.totalTVAC', 'Total TVAC')}</span>
                        <span className="font-heading text-2xl font-bold text-[#C77D63]">{price.toFixed(2)} €</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            <Button
              onClick={submit}
              disabled={submitting}
              data-testid="quick-pay-btn"
              className="w-full h-12 bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  {t('quickBook.pay', 'Payer & confirmer')}
                </>
              )}
            </Button>
            <p className="text-[10px] text-center text-[#1A3C34]/50 mt-3">
              {t('quickBook.secure', 'Paiement sécurisé Stripe — Carte ou Bancontact')}
            </p>
          </section>
        )}
      </div>
    </div>
  );
}
